import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import './GeradorConecta.css';
import {
  renderPreviewHtml, pad4, slug, priorityLabel, listToLines, linesToList,
} from './conecta-render';

// ============================================================================
// MARKETING › GERADOR DO CONECTA
// ============================================================================
// Porte do gerador standalone (gerador_conecta.html) para React de verdade,
// dentro do módulo Marketing. Autoria da newsletter: capa/rodapé são a arte
// FIXA da edição (só nº e data mudam); o Marketing edita comunicados e pessoas.
//
// ⚠️ A arte A4 é renderizada por HTML-string (conecta-render.js) via
//    dangerouslySetInnerHTML — reusar o HTML/CSS exatos é o que garante que a
//    prévia saia idêntica ao PDF. O EDITOR e os MODAIS são React idiomático.
//
// ⚠️ O gerador original era um artifact do Claude e baixava o PDF por
//    `window.claude.use("downloads")`. Aqui o download é padrão do navegador
//    (blob + âncora), e as libs de PDF entram por import dinâmico (só ao clicar).
//
// ⚠️ ESTADO no localStorage (rascunho por navegador). A publicação no portal
//    (POST + imagens no SharePoint) é a PRÓXIMA tarefa (Fatia 2, DEM-00022) —
//    aqui ainda não há "Publicar", só o Baixar PDF que o Marketing já usava.
// ============================================================================

const LS_KEY = 'conecta_gerador_state_v1';
const EDITOR_W_KEY = 'conecta_editor_width_v1';
const EDITOR_W_MIN = 280;
const EDITOR_W_MAX = 900;
const uid = () => Math.random().toString(36).slice(2, 10);
const clone = (o) => JSON.parse(JSON.stringify(o));

function estadoInicial() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw) return JSON.parse(raw);
  } catch { /* localStorage indisponível — cai no padrão */ }
  return { edicao: 1, data: new Date().toISOString().slice(0, 10), comunicados: [], pessoas: [] };
}

function larguraInicial() {
  try {
    const w = parseInt(localStorage.getItem(EDITOR_W_KEY), 10);
    if (w >= EDITOR_W_MIN && w <= EDITOR_W_MAX) return w;
  } catch { /* ignore */ }
  return 400;
}

// Redimensiona um arquivo de imagem para dataURL (mesma lógica do gerador).
function resizeImageFile(file, maxDim) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > maxDim || height > maxDim) {
          if (width > height) { height = Math.round(height * maxDim / width); width = maxDim; }
          else { width = Math.round(width * maxDim / height); height = maxDim; }
        }
        const canvas = document.createElement('canvas');
        canvas.width = width; canvas.height = height;
        canvas.getContext('2d').drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', 0.85));
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
}

// Baixa uma imagem (URL do proxy do Conecta) e devolve como data URI base64 — é
// o que "re-hidrata" uma edição publicada de volta para edição: no banco a imagem
// é só o NOME do arquivo; para editar no gerador (que trabalha em base64) e ver no
// preview, ela volta a ser base64. Ao republicar, é re-externalizada.
function urlParaDataUrl(url) {
  return fetch(url).then((r) => {
    if (!r.ok) throw new Error(String(r.status));
    return r.blob();
  }).then((blob) => new Promise((resolve) => {
    const fr = new FileReader();
    fr.onload = () => resolve(fr.result);
    fr.readAsDataURL(blob);
  }));
}

// Converte uma imagem base64 (JPEG do editor) para WebP base64 SEM prefixo — a
// externalização acontece no NAVEGADOR (canvas), para o backend não depender de
// uma lib nativa de imagem (sharp). O publish sobe esse WebP direto no SharePoint.
function base64ParaWebp(dataUrl) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth; canvas.height = img.naturalHeight;
      canvas.getContext('2d').drawImage(img, 0, 0);
      // toDataURL('image/webp') cai em PNG se o navegador não codificar WebP — os
      // navegadores do parque (Chrome/Edge) codificam; o proxy serve como webp.
      const webp = canvas.toDataURL('image/webp', 0.85);
      resolve(webp.split(',')[1]);
    };
    img.onerror = reject;
    img.src = dataUrl;
  });
}

// Rasteriza os .page em PDF A4 e baixa via blob (troca o window.claude do original).
async function baixarPaginasPdf(pageEls, filename) {
  const [{ default: html2canvas }, { jsPDF }] = await Promise.all([
    import('html2canvas'),
    import('jspdf'),
  ]);
  const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
  for (let i = 0; i < pageEls.length; i++) {
    // eslint-disable-next-line no-await-in-loop -- páginas rasterizadas em ordem
    const canvas = await html2canvas(pageEls[i], { scale: 3, useCORS: true, backgroundColor: '#ffffff' });
    const imgData = canvas.toDataURL('image/jpeg', 0.97);
    if (i > 0) pdf.addPage();
    pdf.addImage(imgData, 'JPEG', 0, 0, 210, 297);
  }
  const blob = pdf.output('blob');
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}

export default function GeradorConecta() {
  const [state, setState] = useState(estadoInicial);
  const [modal, setModal] = useState(null); // { tipo, draft }
  const [editorW, setEditorW] = useState(larguraInicial);
  const [gerando, setGerando] = useState(false);
  const [publicando, setPublicando] = useState(false);
  const [carregando, setCarregando] = useState(false);
  const [modalCarregar, setModalCarregar] = useState(null); // null | lista de edições
  const [aviso, setAviso] = useState('');
  const previewRef = useRef(null);
  const rootRef = useRef(null);

  // Rascunho no navegador.
  useEffect(() => {
    try { localStorage.setItem(LS_KEY, JSON.stringify(state)); } catch { /* ignore */ }
  }, [state]);

  // -------- auto-ajuste do resumo à folha A4 --------
  // A página do comunicado tem altura FIXA (A4) e recorta o que passa
  // (overflow:hidden). Quando ela ganha peso embaixo — a caixa "PRECISA DE AJUDA",
  // a frase "Contamos com a colaboração de todos!", ou um público/atuação longos —,
  // sobra menos espaço para o RESUMO, que então estouraria a folha. Aqui, depois de
  // cada render do preview, medimos se o corpo da página transbordou e reduzimos a
  // fonte do resumo em degraus até caber. Roda no DOM vivo, então o PDF (que
  // rasteriza os `.page`) sai já com a fonte ajustada. É o "aperto no olho" que o
  // gerador original pedia à mão, agora automático — e só mexe no resumo, nunca nos
  // blocos fixos (badges, prioridade, rodapé).
  useLayoutEffect(() => {
    const cont = previewRef.current;
    if (!cont) return undefined;
    // BASE = tamanho de projeto do resumo (bate com o PDF de referência quando as
    // fontes certas estão carregadas). MIN = piso LEGÍVEL: preferimos recortar um
    // fio (como o original fazia) a deixar o texto minúsculo. TOLERANCIA absorve o
    // transbordo imperceptível de 1–2 linhas de sub-pixel, para não encolher uma
    // folha que, no olho, já cabe.
    const BASE = 13.3; const MIN = 11; const PASSO = 0.3; const TOLERANCIA = 4; // px
    const ajustarPagina = (pageEl) => {
      const corpo = pageEl.querySelector('.interior-body');
      const resumo = pageEl.querySelector('.resumo-body');
      if (!corpo || !resumo) return;
      resumo.style.fontSize = `${BASE}px`;
      // O corpo é flex-column com um espaçador flex:1: quando cabe, o espaçador
      // absorve a sobra e scrollHeight == clientHeight; quando estoura, o conteúdo
      // ultrapassa a altura fixa e scrollHeight passa a ser maior. Só mexemos se o
      // transbordo for REAL (acima da tolerância) — senão o resumo fica no BASE.
      if (corpo.scrollHeight <= corpo.clientHeight + TOLERANCIA) return;
      let tam = BASE; let guarda = 0;
      while (corpo.scrollHeight > corpo.clientHeight + TOLERANCIA && tam > MIN && guarda < 40) {
        tam -= PASSO;
        resumo.style.fontSize = `${tam}px`;
        guarda += 1;
      }
    };
    const ajustarTodas = () => {
      cont.querySelectorAll('.page[data-role="comunicado-1"]').forEach(ajustarPagina);
    };
    ajustarTodas();
    // As faixas (topo/rodapé) e as fontes carregam depois da 1ª medição e mudam a
    // altura disponível — re-medir quando ficarem prontas evita medir cedo demais.
    let cancelado = false;
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => { if (!cancelado) ajustarTodas(); });
    }
    const imgs = Array.from(cont.querySelectorAll('img')).filter((im) => !im.complete);
    const aoCarregar = () => { if (!cancelado) ajustarTodas(); };
    imgs.forEach((im) => im.addEventListener('load', aoCarregar));
    return () => {
      cancelado = true;
      imgs.forEach((im) => im.removeEventListener('load', aoCarregar));
    };
  }, [state]);

  const toast = useCallback((msg) => {
    setAviso(msg);
    window.clearTimeout(toast._t);
    toast._t = window.setTimeout(() => setAviso(''), 3200);
  }, []);

  // -------- CRUD imutável sobre o estado --------
  const patch = (p) => setState((s) => ({ ...s, ...p }));

  const salvarComunicado = (draft) => setState((s) => {
    const arr = [...s.comunicados];
    const idx = arr.findIndex((x) => x.id === draft.id);
    if (idx > -1) arr[idx] = draft; else arr.push(draft);
    return { ...s, comunicados: arr };
  });
  const salvarPessoa = (draft) => setState((s) => {
    const arr = [...s.pessoas];
    const idx = arr.findIndex((x) => x.id === draft.id);
    if (idx > -1) arr[idx] = draft; else arr.push(draft);
    return { ...s, pessoas: arr };
  });
  const removerComunicado = (id) => setState((s) => ({ ...s, comunicados: s.comunicados.filter((x) => x.id !== id) }));
  const removerPessoa = (id) => setState((s) => ({ ...s, pessoas: s.pessoas.filter((x) => x.id !== id) }));
  const duplicarComunicado = (id) => setState((s) => {
    const c = s.comunicados.find((x) => x.id === id);
    if (!c) return s;
    const cp = clone(c);
    cp.id = uid();
    (cp.passos || []).forEach((st) => { st.id = uid(); });
    (cp.contatos || []).forEach((ct) => { ct.id = uid(); });
    cp.assunto = `${cp.assunto} (cópia)`;
    const arr = [...s.comunicados];
    arr.splice(arr.indexOf(c) + 1, 0, cp);
    return { ...s, comunicados: arr };
  });
  const mover = (campo, id, dir) => setState((s) => {
    const arr = [...s[campo]];
    const idx = arr.findIndex((x) => x.id === id);
    const swap = idx + dir;
    if (swap < 0 || swap >= arr.length) return s;
    [arr[idx], arr[swap]] = [arr[swap], arr[idx]];
    return { ...s, [campo]: arr };
  });

  const novaEdicao = () => {
    // eslint-disable-next-line no-alert -- confirmação simples de ação destrutiva
    if (!window.confirm('Começar uma edição em branco? Os comunicados e pessoas atuais serão removidos (o número da edição avança).')) return;
    setState((s) => ({
      edicao: (s.edicao || 1) + 1,
      data: new Date().toISOString().slice(0, 10),
      comunicados: [], pessoas: [],
    }));
    toast('Nova edição iniciada em branco.');
  };

  const abrirNovoComunicado = () => setModal({ tipo: 'comunicado', draft: {
    id: uid(), assunto: '', prioridade: 'ALTA', publico: [], atuacao: ['Portal | Grupo Correia'],
    resumo: '', passosTitulo: 'Passo a passo', passos: [], contatos: [],
  } });
  const abrirNovaPessoa = () => setModal({ tipo: 'pessoa', draft: {
    id: uid(), tipo: 'Promoção de Cargo', nome: '', emoji: '🎉', subtitulo: '', texto: '', foto: null, mensagemFinal: '',
  } });

  // -------- PDF --------
  const baixarTudo = async () => {
    setGerando(true);
    try {
      const pages = Array.from(previewRef.current.querySelectorAll('.page'));
      await baixarPaginasPdf(pages, `Conecta_Edicao_${pad4(state.edicao)}.pdf`);
      toast('PDF da edição gerado.');
    } catch (e) {
      console.error(e); toast('Não foi possível gerar o PDF.');
    } finally { setGerando(false); }
  };
  const baixarFicha = async (id) => {
    setGerando(true);
    try {
      const grupo = previewRef.current.querySelector(`.page-group[data-group="${id}"]`);
      const capa = previewRef.current.querySelector('.page-group[data-group="cover"] .page');
      const pages = (capa ? [capa] : []).concat(Array.from(grupo.querySelectorAll('.page')));
      const c = state.comunicados.find((x) => x.id === id);
      const p = state.pessoas.find((x) => x.id === id);
      const nome = c ? `Conecta_${slug(c.assunto)}.pdf` : p ? `Conecta_${slug(p.nome)}.pdf` : 'Conecta_ficha.pdf';
      await baixarPaginasPdf(pages, nome);
      toast('Ficha gerada.');
    } catch (e) {
      console.error(e); toast('Não foi possível gerar o PDF desta ficha.');
    } finally { setGerando(false); }
  };

  // -------- publicar na Biblioteca --------
  // Externaliza as imagens base64 (→ WebP no navegador) e manda o STATE + as
  // imagens para o backend, que sobe no SharePoint e grava em dtbConectaNewsletter.
  const publicar = async () => {
    // eslint-disable-next-line no-alert -- confirmação de ação externa (fica visível para a rede)
    if (!window.confirm(`Publicar a Edição ${pad4(state.edicao)} na Biblioteca? Ela ficará visível para a rede.`)) return;
    setPublicando(true);
    try {
      const estado = clone(state);
      const imagens = {};
      // Nomes DERIVADOS DA POSIÇÃO — têm de casar com o mapeamento da Biblioteca
      // (mapearConecta) e o validador do backend: c<comunicado>-<passo>.webp / p<pessoa>.webp.
      for (let i = 0; i < estado.comunicados.length; i++) {
        const passos = estado.comunicados[i].passos || [];
        for (let j = 0; j < passos.length; j++) {
          const img = passos[j].imagem;
          if (img && /^data:/.test(img)) {
            const nome = `c${i}-${j}.webp`;
            // eslint-disable-next-line no-await-in-loop -- poucas imagens por edição
            imagens[nome] = await base64ParaWebp(img);
            passos[j].imagem = nome; // no banco fica o NOME, não o base64
          }
        }
      }
      for (let k = 0; k < estado.pessoas.length; k++) {
        const foto = estado.pessoas[k].foto;
        if (foto && /^data:/.test(foto)) {
          const nome = `p${k}.webp`;
          // eslint-disable-next-line no-await-in-loop -- poucas imagens por edição
          imagens[nome] = await base64ParaWebp(foto);
          estado.pessoas[k].foto = nome;
        }
      }
      const r = await fetch('/api/biblioteca/conecta', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('authToken')}` },
        body: JSON.stringify({ estado, imagens }),
      });
      if (!r.ok) throw new Error(String(r.status));
      const d = await r.json();
      toast(`Publicado na Biblioteca: ${d.titulo}`);
    } catch (e) {
      console.error(e);
      toast('Não foi possível publicar. Confira o acesso e tente de novo.');
    } finally {
      setPublicando(false);
    }
  };

  // -------- reeditar uma edição já publicada --------
  // Abre a lista de edições publicadas para escolher qual carregar de volta.
  const abrirCarregar = async () => {
    try {
      const r = await fetch('/api/biblioteca/conecta', { headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` } });
      if (!r.ok) throw new Error(String(r.status));
      setModalCarregar(await r.json());
    } catch (e) {
      console.error(e); toast('Não foi possível listar as edições publicadas.');
    }
  };

  // Carrega o STATE de uma edição publicada para o gerador, re-hidratando as
  // imagens (nome no banco → base64) para editar e ver no preview. Republicar a
  // mesma edição sobrescreve (a CHAVE é derivada do número, que volta junto).
  const carregarPublicada = async (chave) => {
    setCarregando(true);
    try {
      const auth = { Authorization: `Bearer ${localStorage.getItem('authToken')}` };
      const [rEd, rTok] = await Promise.all([
        fetch(`/api/biblioteca/conecta/${encodeURIComponent(chave)}`, { headers: auth }),
        fetch(`/api/biblioteca/conecta/${encodeURIComponent(chave)}/token`, { headers: auth }),
      ]);
      if (!rEd.ok || !rTok.ok) throw new Error('fetch');
      const { conteudo } = await rEd.json();
      const { token, baseUrl } = await rTok.json();

      // filename → data URI (ou null se a imagem não existe mais no drive).
      const rehidratar = async (nome) => {
        if (!nome || /^data:/.test(nome)) return nome || null;
        try { return await urlParaDataUrl(`${baseUrl}/${encodeURIComponent(nome)}?t=${encodeURIComponent(token)}`); }
        catch { return null; }
      };
      for (const c of conteudo.comunicados || []) {
        for (const p of (c.passos || [])) {
          // eslint-disable-next-line no-await-in-loop -- poucas imagens por edição
          p.imagem = await rehidratar(p.imagem);
        }
      }
      for (const pe of conteudo.pessoas || []) {
        // eslint-disable-next-line no-await-in-loop -- poucas imagens por edição
        pe.foto = await rehidratar(pe.foto);
      }

      setState({
        edicao: conteudo.edicao || 1,
        data: conteudo.data || new Date().toISOString().slice(0, 10),
        comunicados: conteudo.comunicados || [],
        pessoas: conteudo.pessoas || [],
      });
      setModalCarregar(null);
      toast(`Edição ${pad4(conteudo.edicao || 1)} carregada para edição.`);
    } catch (e) {
      console.error(e); toast('Não foi possível carregar a edição.');
    } finally {
      setCarregando(false);
    }
  };

  // Cliques nos botões "Baixar ficha" que vivem dentro do preview (innerHTML).
  const onPreviewClick = (e) => {
    const btn = e.target.closest('[data-download-group]');
    if (btn && !gerando) baixarFicha(btn.getAttribute('data-download-group'));
  };

  // -------- redimensionar o painel do editor --------
  const arrastando = useRef(false);
  const editorWRef = useRef(editorW);
  useEffect(() => { editorWRef.current = editorW; }, [editorW]);
  const iniciarResize = (e) => {
    arrastando.current = { startX: e.clientX, startW: editorW };
    rootRef.current.classList.add('resizing-editor');
    const onMove = (ev) => {
      if (!arrastando.current) return;
      let w = arrastando.current.startW + (ev.clientX - arrastando.current.startX);
      w = Math.max(EDITOR_W_MIN, Math.min(EDITOR_W_MAX, w));
      setEditorW(w);
    };
    const onUp = () => {
      arrastando.current = false;
      rootRef.current.classList.remove('resizing-editor');
      try { localStorage.setItem(EDITOR_W_KEY, String(editorWRef.current)); } catch { /* ignore */ }
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  return (
    <div className="cnc" ref={rootRef}>
      {/* -------- barra superior -------- */}
      <div className="topbar">
        <h1>Gerador do <span>Conecta</span></h1>
        <div className="field">
          <label htmlFor="cncEdicao">Edição nº</label>
          <input
            id="cncEdicao" type="number" min="1" value={state.edicao}
            onChange={(e) => patch({ edicao: parseInt(e.target.value || '1', 10) || 1 })}
            style={{ width: 70 }}
          />
        </div>
        <div className="field">
          <label htmlFor="cncData">Data de envio</label>
          <input id="cncData" type="date" value={state.data} onChange={(e) => patch({ data: e.target.value })} />
        </div>
        <div className="spacer" />
        {aviso && <span className="saving-note" style={{ color: '#ffd98a' }}>{aviso}</span>}
        <button type="button" className="btn btn-ghost" disabled={carregando} onClick={abrirCarregar}>
          {carregando ? 'Carregando…' : '↧ Carregar publicada'}
        </button>
        <button type="button" className="btn btn-ghost" onClick={novaEdicao}>Nova edição</button>
        <button type="button" className="btn btn-ghost" disabled={gerando} onClick={baixarTudo}>
          {gerando ? 'Gerando PDF…' : '⬇ Baixar edição (PDF)'}
        </button>
        <button type="button" className="btn btn-primary" disabled={publicando} onClick={publicar}>
          {publicando ? 'Publicando…' : '⬆ Publicar no Portal'}
        </button>
      </div>

      {/* -------- editor | preview -------- */}
      <div className="layout">
        <div className="editor" style={{ width: editorW }}>
          <div className="empty-hint" style={{ textAlign: 'left', borderStyle: 'solid', borderColor: '#e6e0d0', background: '#fffaf0', color: '#7a6a3a' }}>
            A capa (página 1) e o rodapé são a arte oficial fixa da edição — só o número e a data (no topo) mudam. Edite os comunicados e pessoas abaixo.
          </div>

          <h2>Comunicados <button type="button" className="btn btn-sm btn-primary" onClick={abrirNovoComunicado}>+ novo</button></h2>
          {state.comunicados.length === 0 && <div className="empty-hint">Nenhum comunicado ainda.</div>}
          {state.comunicados.map((c, i) => (
            <div className="editor-card" key={c.id}>
              <div className="row1"><div className="titlebox">{c.assunto || '(sem título)'}</div></div>
              <div className="metabox">
                <span className={`tag tag-${(c.prioridade || 'baixa').toLowerCase()}`}>{priorityLabel(c.prioridade)}</span>
                <span>{c.passos && c.passos.length ? `${c.passos.length} passo(s)` : 'sem passo a passo'}</span>
              </div>
              <div className="btnrow">
                <button type="button" className="btn btn-sm btn-ghost" onClick={() => setModal({ tipo: 'comunicado', draft: clone(c) })}>Editar</button>
                <button type="button" className="btn btn-sm btn-ghost" onClick={() => duplicarComunicado(c.id)}>Duplicar</button>
                <button type="button" className="btn btn-sm btn-ghost" disabled={i === 0} onClick={() => mover('comunicados', c.id, -1)}>▲</button>
                <button type="button" className="btn btn-sm btn-ghost" disabled={i === state.comunicados.length - 1} onClick={() => mover('comunicados', c.id, 1)}>▼</button>
                <button type="button" className="btn btn-sm btn-ghost" disabled={gerando} onClick={() => baixarFicha(c.id)}>⬇ PDF</button>
                {/* eslint-disable-next-line no-alert -- confirmação simples */}
                <button type="button" className="btn btn-sm btn-danger" onClick={() => { if (window.confirm(`Remover o comunicado "${c.assunto || ''}"?`)) removerComunicado(c.id); }}>Remover</button>
              </div>
            </div>
          ))}

          <h2>Pessoas e Conquistas <button type="button" className="btn btn-sm btn-primary" onClick={abrirNovaPessoa}>+ novo</button></h2>
          {state.pessoas.length === 0 && <div className="empty-hint">Nenhuma pessoa/conquista ainda.</div>}
          {state.pessoas.map((p, i) => (
            <div className="editor-card" key={p.id}>
              <div className="row1"><div className="titlebox">{p.tipo} - {p.nome}</div></div>
              <div className="btnrow">
                <button type="button" className="btn btn-sm btn-ghost" onClick={() => setModal({ tipo: 'pessoa', draft: clone(p) })}>Editar</button>
                <button type="button" className="btn btn-sm btn-ghost" disabled={i === 0} onClick={() => mover('pessoas', p.id, -1)}>▲</button>
                <button type="button" className="btn btn-sm btn-ghost" disabled={i === state.pessoas.length - 1} onClick={() => mover('pessoas', p.id, 1)}>▼</button>
                <button type="button" className="btn btn-sm btn-ghost" disabled={gerando} onClick={() => baixarFicha(p.id)}>⬇ PDF</button>
                {/* eslint-disable-next-line no-alert -- confirmação simples */}
                <button type="button" className="btn btn-sm btn-danger" onClick={() => { if (window.confirm(`Remover "${p.nome}"?`)) removerPessoa(p.id); }}>Remover</button>
              </div>
            </div>
          ))}
        </div>

        <div className="editor-resize-handle" onMouseDown={iniciarResize} onDoubleClick={() => setEditorW(400)} />

        <div
          className="preview" ref={previewRef} onClick={onPreviewClick}
          dangerouslySetInnerHTML={{ __html: renderPreviewHtml(state) }}
        />
      </div>

      {modal && modal.tipo === 'comunicado' && (
        <ComunicadoModal
          inicial={modal.draft}
          onCancelar={() => setModal(null)}
          onSalvar={(d) => { salvarComunicado(d); setModal(null); toast('Comunicado salvo.'); }}
        />
      )}
      {modal && modal.tipo === 'pessoa' && (
        <PessoaModal
          inicial={modal.draft}
          onCancelar={() => setModal(null)}
          onSalvar={(d) => { salvarPessoa(d); setModal(null); toast('Salvo.'); }}
        />
      )}
      {modalCarregar && (
        <Backdrop onClose={() => setModalCarregar(null)}>
          <h3>Carregar edição publicada</h3>
          <p className="hint" style={{ marginBottom: 14 }}>
            Escolha uma edição já publicada para editar de novo. ⚠️ Isto SUBSTITUI o rascunho atual do gerador. Ao republicar, a mesma edição é sobrescrita.
          </p>
          {modalCarregar.length === 0 && <div className="empty-hint">Nenhuma edição publicada ainda.</div>}
          {modalCarregar.map((ed) => (
            <div className="editor-card" key={ed.CHAVE} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
              <div>
                <div className="titlebox">{ed.TITULO}</div>
                <div className="metabox">{String(ed.DATA_LANCAMENTO || '').slice(0, 10).split('-').reverse().join('/')}{ed.MODULO ? ` · ${ed.MODULO}` : ''}</div>
              </div>
              <button type="button" className="btn btn-sm btn-primary" disabled={carregando} onClick={() => carregarPublicada(ed.CHAVE)}>Carregar</button>
            </div>
          ))}
          <div className="cnc-modal-actions">
            <button type="button" className="btn btn-ghost" onClick={() => setModalCarregar(null)}>Fechar</button>
          </div>
        </Backdrop>
      )}
    </div>
  );
}

// ============================================================================
// MODAL — Comunicado
// ============================================================================
function ComunicadoModal({ inicial, onSalvar, onCancelar }) {
  const [d, setD] = useState(inicial);
  const set = (campo) => (e) => setD({ ...d, [campo]: e.target.value });

  const setPasso = (id, campo, valor) => setD((s) => ({ ...s, passos: s.passos.map((p) => (p.id === id ? { ...p, [campo]: valor } : p)) }));
  const addPasso = () => setD((s) => ({ ...s, passos: [...s.passos, { id: uid(), titulo: '', descricao: '', imagem: null }] }));
  const rmPasso = (id) => setD((s) => ({ ...s, passos: s.passos.filter((p) => p.id !== id) }));
  const imgPasso = async (id, file) => { if (file) setPasso(id, 'imagem', await resizeImageFile(file, 640)); };

  const setContato = (id, campo, valor) => setD((s) => ({ ...s, contatos: s.contatos.map((c) => (c.id === id ? { ...c, [campo]: valor } : c)) }));
  const addContato = () => setD((s) => ({ ...s, contatos: [...s.contatos, { id: uid(), nome: '', cargo: '', email: '' }] }));
  const rmContato = (id) => setD((s) => ({ ...s, contatos: s.contatos.filter((c) => c.id !== id) }));

  const salvar = () => {
    const limpo = { ...d, assunto: (d.assunto || '').trim(), passosTitulo: (d.passosTitulo || '').trim() };
    if (!limpo.assunto) { window.alert('Preencha o assunto do comunicado.'); return; } // eslint-disable-line no-alert
    delete limpo._isNew;
    onSalvar(limpo);
  };

  return (
    <Backdrop onClose={onCancelar}>
      <h3>{inicial.assunto ? 'Editar comunicado' : 'Novo comunicado'}</h3>
      <div className="fgroup"><label>Assunto (título)</label><input type="text" value={d.assunto} onChange={set('assunto')} /></div>
      <div className="fgroup">
        <label>Prioridade</label>
        <div className="radiorow">
          {['ALTA', 'MEDIA', 'BAIXA'].map((p) => (
            <label key={p}><input type="radio" name="cncPrio" value={p} checked={d.prioridade === p} onChange={() => setD({ ...d, prioridade: p })} /> {priorityLabel(p)}</label>
          ))}
        </div>
      </div>
      <div className="fgroup"><label>Público (um por linha)</label><textarea value={listToLines(d.publico)} onChange={(e) => setD({ ...d, publico: linesToList(e.target.value) })} /></div>
      <div className="fgroup"><label>Atuação (um por linha)</label><textarea value={listToLines(d.atuacao)} onChange={(e) => setD({ ...d, atuacao: linesToList(e.target.value) })} /></div>
      <div className="fgroup"><label>Resumo (parágrafos separados por linha em branco; use • ou "* " no início da linha para tópico, **negrito**, *itálico*)</label><textarea style={{ minHeight: 140 }} value={d.resumo} onChange={set('resumo')} /></div>
      <div className="fgroup"><label>Título do passo a passo (deixe os passos vazios se não precisar)</label><input type="text" value={d.passosTitulo} onChange={set('passosTitulo')} /></div>

      <div className="fgroup">
        <label>Passos</label>
        {d.passos.map((p, i) => (
          <div className="step-fieldset" key={p.id}>
            <div className="fhead"><b>Passo {i + 1}</b><button type="button" className="btn btn-sm btn-danger" onClick={() => rmPasso(p.id)}>Remover</button></div>
            <div className="fgroup"><label>Título</label><input type="text" value={p.titulo} onChange={(e) => setPasso(p.id, 'titulo', e.target.value)} /></div>
            <div className="fgroup"><label>Descrição</label><textarea value={p.descricao} onChange={(e) => setPasso(p.id, 'descricao', e.target.value)} /></div>
            <div className="fgroup">
              <label>Imagem (opcional)</label>
              <div className="imgpick">
                {p.imagem ? <img src={p.imagem} alt="" /> : <div className="noimg">sem imagem</div>}
                <input type="file" accept="image/*" onChange={(e) => imgPasso(p.id, e.target.files[0])} />
                {p.imagem && <button type="button" className="btn btn-sm btn-ghost" onClick={() => setPasso(p.id, 'imagem', null)}>remover imagem</button>}
              </div>
            </div>
          </div>
        ))}
        <button type="button" className="btn btn-sm btn-ghost" onClick={addPasso}>+ adicionar passo</button>
      </div>

      <div className="fgroup">
        <label>Contatos de ajuda (opcional — some se vazio)</label>
        {d.contatos.map((c) => (
          <div className="subitem" key={c.id}>
            <input type="text" placeholder="Nome" value={c.nome} onChange={(e) => setContato(c.id, 'nome', e.target.value)} />
            <input type="text" placeholder="Cargo" value={c.cargo} onChange={(e) => setContato(c.id, 'cargo', e.target.value)} />
            <input type="email" placeholder="E-mail (Teams)" value={c.email || ''} onChange={(e) => setContato(c.id, 'email', e.target.value)} style={{ minWidth: 150 }} />
            <button type="button" className="btn btn-sm btn-danger rm" onClick={() => rmContato(c.id)}>✕</button>
          </div>
        ))}
        <div className="hint">O e-mail não aparece no comunicado — só é usado para o nome do contato abrir uma conversa no Teams.</div>
        <button type="button" className="btn btn-sm btn-ghost" onClick={addContato}>+ adicionar contato</button>
      </div>

      <div className="fgroup"><label>Frase de assinatura (rodapé; apague para não exibir)</label><input type="text" value={d.frase === undefined ? 'Contamos com a colaboração de todos!' : d.frase} onChange={set('frase')} /></div>

      <div className="cnc-modal-actions">
        <button type="button" className="btn btn-ghost" onClick={onCancelar}>Cancelar</button>
        <button type="button" className="btn btn-primary" onClick={salvar}>Salvar</button>
      </div>
    </Backdrop>
  );
}

// ============================================================================
// MODAL — Pessoa/Conquista
// ============================================================================
function PessoaModal({ inicial, onSalvar, onCancelar }) {
  const [d, setD] = useState(inicial);
  const set = (campo) => (e) => setD({ ...d, [campo]: e.target.value });
  const setFoto = async (file) => {
    if (!file) return;
    const url = await resizeImageFile(file, 700);
    setD((s) => ({ ...s, foto: url }));
  };

  const salvar = () => {
    const limpo = { ...d, tipo: (d.tipo || '').trim(), nome: (d.nome || '').trim() };
    if (!limpo.nome) { window.alert('Preencha o nome.'); return; } // eslint-disable-line no-alert
    delete limpo._isNew;
    onSalvar(limpo);
  };

  return (
    <Backdrop onClose={onCancelar}>
      <h3>{inicial.nome ? 'Editar pessoa/conquista' : 'Nova pessoa/conquista'}</h3>
      <div className="fgroup"><label>Tipo (ex.: Promoção de Cargo, Bem-vindo(a))</label><input type="text" value={d.tipo} onChange={set('tipo')} /></div>
      <div className="fgroup"><label>Nome</label><input type="text" value={d.nome} onChange={set('nome')} /></div>
      <div className="fgroup"><label>Emoji (opcional)</label><input type="text" value={d.emoji || ''} onChange={set('emoji')} style={{ width: 70 }} /></div>
      <div className="fgroup"><label>Subtítulo</label><input type="text" value={d.subtitulo || ''} onChange={set('subtitulo')} /></div>
      <div className="fgroup"><label>Texto (parágrafos separados por linha em branco)</label><textarea style={{ minHeight: 120 }} value={d.texto} onChange={set('texto')} /></div>
      <div className="fgroup">
        <label>Arte/foto da promoção (imagem pronta, ex. do Canva)</label>
        <div className="imgpick">
          {d.foto ? <img src={d.foto} alt="" /> : <div className="noimg">sem imagem</div>}
          <input type="file" accept="image/*" onChange={(e) => setFoto(e.target.files[0])} />
        </div>
      </div>
      <div className="fgroup"><label>Mensagem final</label><textarea value={d.mensagemFinal || ''} onChange={set('mensagemFinal')} /></div>
      <div className="cnc-modal-actions">
        <button type="button" className="btn btn-ghost" onClick={onCancelar}>Cancelar</button>
        <button type="button" className="btn btn-primary" onClick={salvar}>Salvar</button>
      </div>
    </Backdrop>
  );
}

// Backdrop do modal — fecha ao clicar fora.
function Backdrop({ children, onClose }) {
  return (
    <div className="cnc-modal-backdrop" onMouseDown={(e) => { if (e.target.classList.contains('cnc-modal-backdrop')) onClose(); }}>
      <div className="cnc-modal">{children}</div>
    </div>
  );
}
